import { useState } from "react";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { useAppDispatch } from "../../hooks";
import { markAsViewed } from "../../slices/log";
import { openLoginPrompt } from "../../slices/auth";
import FavouriteButton from "./FavouriteButton";
import VideoModal, { PlayingVideo } from "./VideoModal";
import { CardObjectType, isFavouritable } from "../../types/content";
import { buildYoutubeEmbedUrl } from "../../utils/youtube";

export default function CardWithImageTopAndFooter({
  image,
  middleContent,
  footerContent,
  bgColour = "off-white",
  href = "#!",
  objectId,
  objectType,
  showFavouriteButton = true,
  dataTitle,
  dataAuthor,
  dataTags,
  openOnCardClick = true,
  locked = false,
  size = "grid",
}: {
  image: string;
  middleContent: React.ReactNode;
  /**
   * Either plain nodes, or a render function handed `openContent` so the footer
   * can own the navigation with its own control (see `openOnCardClick`).
   */
  footerContent:
    | React.ReactNode
    | ((
        openContent: (e?: React.MouseEvent) => void,
        /** False when `openContent` plays in place rather than opening a tab. */
        opensInNewTab: boolean,
      ) => React.ReactNode);
  bgColour?: string;
  href?: string | null;
  objectId: string;
  objectType: CardObjectType;
  showFavouriteButton?: boolean;
  dataTitle: string;
  dataAuthor: string;
  dataTags: string;
  /**
   * When false the card body is inert and only whatever the footer wires up to
   * `openContent` opens the content. Cards whose footer has no such control
   * (people, directory entries…) leave this on, otherwise they become dead ends.
   */
  openOnCardClick?: boolean;
  /**
   * Members-only content the current visitor cannot open. The card still
   * renders as a teaser; every route into the content asks them to sign in
   * instead. The API withholds the href from a locked payload regardless, so
   * this governs what the card says, not whether the gate holds.
   */
  locked?: boolean;
  /**
   * How large the card renders. `grid` is the tile every content grid uses;
   * `hero` is the wide variant a landing section leads with.
   *
   * Fixed variants rather than free-form width/height props because Tailwind only
   * emits classes it can read as literal strings — the interpolated
   * `max-w-${maxWidth}` / `h-${height}` the old hero card carried compiled to
   * nothing at all, so the "custom" size was never really applied.
   */
  size?: "grid" | "hero";
}) {
  const dispatch = useAppDispatch();

  // `href = "#!"` above only covers undefined. A members-only payload has its
  // url withheld and arrives as null, so normalise before touching it -- reading
  // .includes() off null threw during render and blanked the entire page.
  const link = href ?? "#!";
  const isYoutube = link.includes("youtube") || link.includes("youtu.be");

  const [playingVideo, setPlayingVideo] = useState<PlayingVideo | null>(null);

  /**
   * The player URL for this card, or null when the content cannot be embedded.
   *
   * Null covers more than "not a video": a YouTube playlist or channel link has no
   * single video to play, so those keep opening on YouTube rather than showing an
   * empty frame.
   */
  const embedUrl = isYoutube ? buildYoutubeEmbedUrl(link) : null;

  /**
   * Open the content and record the visit.
   *
   * Kept here rather than in each caller so a footer button and a card click log
   * the same analytics event — the two used to be the same code path and the
   * dashboards still assume one "click" per opened item. That holds whether the
   * content plays here or opens in a tab: the event fires before either.
   */
  function openContent(e?: React.MouseEvent) {
    e?.preventDefault();
    e?.stopPropagation();

    // Members-only: ask for a sign-in rather than opening anything. No view is
    // logged either -- nothing was viewed.
    if (locked) {
      dispatch(openLoginPrompt());
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click",
      item: "content",
      contentTitle: dataTitle,
      contentAuthor: dataAuthor,
      contentTags: dataTags,
    });
    dispatch(markAsViewed({ objectId, objectType }));

    if (embedUrl) {
      setPlayingVideo({
        embedUrl,
        watchUrl: link,
        title: dataTitle,
        author: dataAuthor,
      });
      return;
    }

    window.open(link, "_blank", "noopener,noreferrer");
  }

  // map allowed bgColour values to Tailwind classes (prevents JIT purge)
  const bgClass = {
    "off-white": "bg-off-white",
    white: "bg-white",
    black: "bg-black",
    "gray-50": "bg-gray-50",
    // Every content card asks for this one; without the mapping they all fell
    // through to the off-white default.
    "dark-beige": "bg-dark-beige",
    // add more mappings you need
  }[bgColour] ?? "bg-off-white";

  // Same reason as bgClass: written out in full so Tailwind sees each class.
  const sizeClass = {
    grid: "max-w-[300px] h-[400px]",
    hero: "max-w-full h-[600px]",
  }[size];

  return (
    <>
    <div
      className={`
        content-card
        mx-auto flex-none
        w-full
        ${sizeClass}
        ${bgClass}
        rounded-3xl
        grid grid-rows-[auto_1fr_60px]   /* top row auto so it sizes to the spacer (16:9) */
        ${openOnCardClick ? "cursor-pointer" : ""}
        overflow-hidden
      `}
      data-title={dataTitle}
      data-author={dataAuthor}
      data-tags={dataTags}
      onClick={openOnCardClick ? () => openContent() : undefined}
    >
      {/* Top: Image area that preserves 16:9 using spacer trick */}
      <div className="relative w-full overflow-hidden rounded-t-3xl">
        {/* Spacer keeps the container 16:9 (56.25% = 9/16) */}
        <div className="w-full pt-[56.25%]" />

        {/* Absolutely positioned content fills the spacer area */}
        <div className="absolute inset-0 w-full h-full">
          {isYoutube ? (
            <>
              <img
                src={image}
                alt={dataTitle}
                className="w-full h-full object-contain bg-black"
              />
              {/* The play badge opens the content itself rather than being
                  decoration over an inert thumbnail. On a card with
                  `openOnCardClick={false}` the image swallowed the click, so the
                  one control that looks like a play button did nothing. */}
              <button
                type="button"
                onClick={openContent}
                aria-label={
                  locked
                    ? `Sign in to watch "${dataTitle}"`
                    : `Play "${dataTitle}" ${
                        embedUrl ? "(plays on this page)" : "(opens in a new tab)"
                      }`
                }
                className="
                  absolute top-1/2 left-1/2 w-1/5 aspect-square
                  -translate-x-1/2 -translate-y-1/2
                  transition-transform duration-200 ease-out
                  hover:scale-110 active:scale-95
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black
                  motion-reduce:transform-none motion-reduce:transition-none
                "
              >
                <img
                  src="/img/graphics/images/youtube-icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-contain"
                />
              </button>
            </>
          ) : (
            <img
              src={image}
              alt={dataTitle}
              className="w-full h-full object-cover"
            />
          )}

          {/* `isFavouritable` gate: a card's objectType is also used for view
              logging and ratings, and "Support" participates in those without
              being favouritable (Profile has no favourite_supports field, and the
              API's get_model does not map it). Rendering a heart for it produced a
              control that could never fill and answered clicks with a 400. */}
          {/* Members-only marker. Sits with the favourite button so it reads as
              part of the card's status row rather than as an error. */}
          {locked && (
            <span className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-dark-blue/90 px-2 py-1 text-xxs font-bold text-white">
              <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
              Members only
            </span>
          )}

          {showFavouriteButton && isFavouritable(objectType) && (
            <FavouriteButton
              objectId={objectId}
              objectType={objectType}
              dataTitle={dataTitle}
              dataAuthor={dataAuthor}
              dataTags={dataTags}
            />
          )}
        </div>
      </div>

      {/* Middle: Scrollable text.
          The anchor routes through `openContent` instead of navigating itself:
          its click also bubbles to the card, so letting both run opened the same
          href in two tabs. Dropped entirely when the card is inert. */}
      <div className="overflow-y-auto scrollbar-invisible p-4 text-dark-grey text-left">
        {openOnCardClick ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="grid grid-cols-1 gap-y-2"
            onClick={openContent}
          >
            {middleContent}
          </a>
        ) : (
          <div className="grid grid-cols-1 gap-y-2">{middleContent}</div>
        )}
      </div>

      {/* Bottom: Fixed actions area */}
      <div className="flex items-center justify-start gap-x-4 px-4 py-2 rounded-b-3xl">
        {typeof footerContent === "function"
          ? footerContent(openContent, locked ? false : embedUrl === null)
          : footerContent}
      </div>
    </div>

    <VideoModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
    </>
  );
}
